import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { TrackingEventsDialogTrigger } from "@/components/internal/shared/TrackingEventsDialogTrigger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCarriers, useSamples } from "@/hooks/useSamples";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { useProducts } from "@/hooks/useProducts";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { formatDate, formatStatusLabel } from "@/lib/format";
import { exportToCSV } from "@/lib/export";
import { hasPermission } from "@/lib/roles";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getSampleStatusBadgeClass } from "@/lib/status-tag-colors";
import { useAuthIdentity } from "@/contexts/UserProfileContext";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "rejected", label: "Rejected" },
] as const;

const REQUEST_STATUSES = new Set(["requested", "pending"]);
const APPROVED_STATUSES = new Set(["approved"]);

function normalizeStatus(status?: string) {
  return (status ?? "").toLowerCase();
}

function isLocalPickupTracking(value?: string) {
  const normalized = (value ?? "").trim().toLowerCase();
  return normalized === "local pick up" || normalized === "local pickup" || normalized === "pickup";
}

function formatSampleSizeLabel(sampleType?: string, quantity?: number | null) {
  if (sampleType === "Green") {
    return "200 g";
  }

  if (sampleType === "Roasted") {
    return "100 g";
  }

  if (typeof quantity === "number" && Number.isFinite(quantity) && quantity > 0) {
    const rounded = Number(quantity >= 1 ? quantity.toFixed(2) : quantity.toFixed(3));
    return `${rounded.toString()} lb${Math.abs(quantity - 1) < 0.001 ? "" : "s"}`;
  }

  return "-";
}

/**
 * Renders the expanded row content showing multi-item breakdown for a sample request
 */
function SampleItemsExpanded({ row }: { row: any }) {
  const items: any[] = row.original.items ?? [];

  const gridClass = "grid grid-cols-[minmax(240px,2.1fr)_minmax(120px,0.8fr)_minmax(80px,0.5fr)_minmax(180px,1.2fr)] gap-3";

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">No items</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-2">
      <div className={`${gridClass} rounded-md border bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground`}>
        <span className="truncate">Product</span>
        <span className="truncate">Type</span>
        <span className="truncate">Sample Size</span>
        <span className="truncate">Notes</span>
      </div>
      <div className="space-y-1">
        {items.map((item: any, idx: number) => (
          <div key={item._id ?? idx} className={`${gridClass} rounded-md px-3 py-2 text-sm`}>
            <span className="min-w-0 truncate">{item.productName ?? item.product?.name ?? "—"}</span>
            <span className="min-w-0 truncate">{item.sampleType ?? "—"}</span>
            <span className="min-w-0">{formatSampleSizeLabel(item.sampleType, item.quantity)}</span>
            <span className="min-w-0 truncate text-muted-foreground">{item.notes ?? "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function useColumns({
  onApprove,
  onFulfill,
  onReject,
  onTrackingDelivered,
  canManageSamples,
}: {
  onApprove: (row: any) => void;
  onFulfill: (row: any) => void;
  onReject: (row: any) => void;
  onTrackingDelivered: (row: any) => Promise<void>;
  canManageSamples: boolean;
}) {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getSampleStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select",
          options: [
            { label: "Pending", value: "pending" },
            { label: "Approved", value: "approved" },
            { label: "Shipped", value: "shipped" },
            { label: "Delivered", value: "delivered" },
            { label: "Rejected", value: "rejected" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorFn: (row) => row.user?.company || row.user?.name || row.contactEmail || row.user?.email || "Unknown",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Customer" />,
        cell: ({ row }) => {
          const company = row.original.user?.company || "";
          const customerName = row.original.user?.name || row.original.contactEmail || row.original.user?.email || "Unknown";
          return (
            <div className="flex flex-col">
              <span className="text-xs font-medium">
                {company || customerName}
              </span>
              {company && company !== customerName && (
                <span className="text-xs text-muted-foreground">
                  {customerName}
                </span>
              )}
            </div>
          );
        },
        meta: { label: "Customer", placeholder: "Search customer...", variant: "text" },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue) => {
          const query = String(filterValue ?? "").toLowerCase().trim();
          if (!query) return true;

          return [
            row.original.user?.company,
            row.original.user?.name,
            row.original.contactEmail,
            row.original.user?.email,
            row.original.user?._id,
            row.original.userId,
          ].some((value) => String(value ?? "").toLowerCase().includes(query));
        },
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => Boolean(row.user?.isEnterprise),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Enterprise" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.user?.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.user?.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select",
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((value: string) => value === String(Boolean(row.original.user?.isEnterprise))),
      },
      {
        id: "email",
        accessorFn: (row) => row.contactEmail || row.user?.email || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Email" />,
        meta: { label: "Email", placeholder: "Search email...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "items",
        accessorFn: (row) => row.itemCount ?? (row.items?.length ?? 0),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Items" />,
        cell: ({ row }) => {
          const count = row.original.itemCount ?? row.original.items?.length ?? 0;
          const names = (row.original.items ?? [])
            .slice(0, 2)
            .map((i: any) => i.productName ?? i.product?.name ?? "")
            .filter(Boolean)
            .join(", ");
          return (
            <div className="flex flex-col">
              <span className="text-xs font-medium">{count} item{count !== 1 ? "s" : ""}</span>
              {names && <span className="max-w-[160px] truncate text-xs text-muted-foreground">{names}</span>}
            </div>
          );
        },
      },
      {
        id: "trackingNumber",
        accessorKey: "trackingNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Tracking #" />,
        cell: ({ row }) => {
          const trackingNumber = String(row.original.trackingNumber ?? "").trim();
          if (!trackingNumber) return <span className="text-muted-foreground">-</span>;
          if (isLocalPickupTracking(trackingNumber)) {
            return <span className="text-xs">{trackingNumber}</span>;
          }
          return (
            <TrackingEventsDialogTrigger
              trackingNumber={trackingNumber}
              carrierName={row.original.carrier?.name}
              carrierCode={row.original.carrier?.shippeekCode}
              triggerClassName="text-xs"
              onDelivered={
                normalizeStatus(row.original.status) === "delivered"
                  ? undefined
                  : () => onTrackingDelivered(row.original)
              }
            />
          );
        },
        meta: { label: "Tracking", placeholder: "Search tracking...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Requested" />,
        cell: ({ row }) => {
          const ts = row.original.createdAt || row.original._creationTime;
          return ts ? <span className="text-sm">{formatDate(ts)}</span> : "-";
        },
        meta: { label: "Requested", variant: "date" },
        enableColumnFilter: true,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          if (!canManageSamples) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }

          const status = normalizeStatus(row.original.status);
          const isPending = REQUEST_STATUSES.has(status);
          const isApproved = APPROVED_STATUSES.has(status);
          return (
            <div className="flex items-center gap-1">
              {isPending && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => onApprove(row.original)}
                >
                  Approve
                </Button>
              )}
              {(isPending || isApproved) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => onFulfill(row.original)}
                >
                  Fulfill
                </Button>
              )}
              {isPending && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 px-2 text-xs"
                  onClick={() => onReject(row.original)}
                >
                  Reject
                </Button>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [canManageSamples, onApprove, onFulfill, onReject, onTrackingDelivered],
  );
}

export default function SalesSamplesPage() {
  const allSamples = useSamples(200);
  const carriers = useCarriers();
  const users = useUsersForImpersonation();
  const products = useProducts();
  const { authUserId, userRole, isPending } = useAuthIdentity();
  const [searchParams] = useSearchParams();
  const customerIdFilter = searchParams.get("customerId")?.trim() ?? "";
  const canManageSamples = hasPermission(userRole, "samples:edit");
  const adminProducts = useQuery(
    api.admin.getAllProducts,
    authUserId && userRole === "admin" ? { authUserId } : "skip",
  );
  const createMutation = useMutation(api.samples.createSampleRequest);
  const approveMutation = useMutation(api.samples.approveSampleRequest);
  const rejectMutation = useMutation(api.samples.rejectSampleRequest);
  const fulfillMutation = useMutation(api.samples.fulfillSampleRequest);
  const updateSampleMutation = useMutation(api.samples.updateSampleRequest);
  const bulkUpdateMutation = useMutation(api.samples.bulkUpdateSampleStatus);

  const [activeTab, setActiveTab] = React.useState("all");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [form, setForm] = React.useState({ userId: "", productId: "", quantity: 1, notes: "" });
  const [isCreating, setIsCreating] = React.useState(false);
  const [activeBulkAction, setActiveBulkAction] = React.useState<"approve" | "undoApprove" | "ship" | "reject" | null>(null);

  // --- Reject dialog (single row) ---
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");
  const [rejectSampleIds, setRejectSampleIds] = React.useState<string[]>([]);
  const rejectClearSelectionRef = React.useRef<(() => void) | null>(null);

  // --- Ship/Fulfill dialog ---
  const [shipOpen, setShipOpen] = React.useState(false);
  const [shipSampleIds, setShipSampleIds] = React.useState<string[]>([]);
  const shipClearSelectionRef = React.useRef<(() => void) | null>(null);
  const [shipForm, setShipForm] = React.useState({
    trackingNumber: "",
    carrierId: "",
    localPickup: false,
  });

  // --- Approve dialog (single row) ---
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [approveSampleId, setApproveSampleId] = React.useState<string | null>(null);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [isApproving, setIsApproving] = React.useState(false);

  // --- Fulfill dialog (single row) ---
  const [fulfillOpen, setFulfillOpen] = React.useState(false);
  const [fulfillSampleId, setFulfillSampleId] = React.useState<string | null>(null);
  const [fulfillForm, setFulfillForm] = React.useState({
    trackingNumber: "",
    carrierId: "",
    notes: "",
    localPickup: false,
  });
  const [isFulfilling, setIsFulfilling] = React.useState(false);

  // --- Reject dialog (single row) ---
  const [singleRejectOpen, setSingleRejectOpen] = React.useState(false);
  const [singleRejectId, setSingleRejectId] = React.useState<string | null>(null);
  const [singleRejectReason, setSingleRejectReason] = React.useState("");
  const [isRejecting, setIsRejecting] = React.useState(false);

  const filteredSamples = React.useMemo(() => {
    if (!allSamples) return [];
    if (activeTab === "all") return allSamples;
    return allSamples.filter((s: any) => {
      const status = String(s.status ?? "").toLowerCase();
      if (activeTab === "pending") return status === "requested" || status === "pending";
      if (activeTab === "rejected") return status === "rejected" || status === "cancelled";
      return status === activeTab;
    });
  }, [allSamples, activeTab]);

  const availableProducts = React.useMemo(() => {
    if (Array.isArray(adminProducts) && adminProducts.length > 0) {
      return adminProducts;
    }
    return products ?? [];
  }, [adminProducts, products]);

  const customerOptions = React.useMemo(() => {
    return buildCustomerComboboxOptions(users as any[] | undefined);
  }, [users]);

  const productComboboxOptions = React.useMemo(() => {
    return availableProducts.map((product: any) => {
      const originName = String(product?.origin?.name ?? "").trim();
      const producerName = String(product?.producer?.name ?? "").trim();
      const sublabel = [originName, producerName].filter(Boolean).join(" · ");
      const searchText = [product?.name, originName, producerName].filter(Boolean).join(" ");

      return {
        value: product._id,
        label: String(product?.name ?? ""),
        sublabel: sublabel || undefined,
        searchText: searchText || undefined,
      };
    });
  }, [availableProducts]);

  const isLoadingProducts = userRole === "admin" ? adminProducts === undefined : products === undefined;

  const handleCreate = React.useCallback(async () => {
    if (!form.userId || !form.productId) {
      toast.error("Please select a customer and product");
      return;
    }
    setIsCreating(true);
    try {
      await createMutation({
        userId: form.userId as any,
        productId: form.productId as any,
        quantity: form.quantity,
        notes: form.notes || undefined,
        message: form.notes || undefined,
      });
      toast.success("Sample request created");
      setCreateOpen(false);
      setForm({ userId: "", productId: "", quantity: 1, notes: "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create sample");
    } finally {
      setIsCreating(false);
    }
  }, [form, createMutation]);

  const handleExport = React.useCallback(() => {
    if (!allSamples) return;
    exportToCSV(
      allSamples.map((sample: any) => ({
        ...sample,
        enterprise: getEnterpriseBadgeLabel(sample?.user?.isEnterprise),
      })),
      [
        { key: "status", label: "Status" },
        { key: "enterprise", label: "Enterprise" },
        { key: "contactEmail", label: "Email" },
        { key: "sampleType", label: "Type" },
        { key: "trackingNumber", label: "Tracking #" },
      ],
      "sales-samples.csv",
    );
  }, [allSamples]);

  const getSelectedSampleIds = React.useCallback((selectedRows: any[]) => {
    return selectedRows.map((row) => row._id ?? row.id).filter(Boolean);
  }, []);

  const handleBulkStatusChange = React.useCallback(async (
    selectedRows: any[],
    newStatus: "approved" | "pending",
    action: "approve" | "undoApprove",
    successLabel: string,
    clearSelection: () => void,
  ) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error("No samples selected");
      return;
    }

    setActiveBulkAction(action);
    try {
      const result = await bulkUpdateMutation({
        sampleRequestIds: ids as any[],
        status: newStatus,
      });
      const updated = result?.data?.updated ?? ids.length;
      toast.success(`${successLabel} ${updated} sample${updated === 1 ? "" : "s"}`);
      clearSelection();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update selected samples",
      );
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, getSelectedSampleIds]);

  const openShipDialog = React.useCallback((selectedRows: any[], clearSelection: () => void) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error("No samples selected");
      return;
    }

    const first = selectedRows[0];
    const firstTracking = String(first?.trackingNumber ?? "");
    const localPickup = isLocalPickupTracking(firstTracking);

    shipClearSelectionRef.current = clearSelection;
    setShipSampleIds(ids as string[]);
    setShipForm({
      trackingNumber: localPickup ? "" : firstTracking,
      carrierId: String(first?.carrierId ?? ""),
      localPickup,
    });
    setShipOpen(true);
  }, [getSelectedSampleIds]);

  const handleShipSelected = React.useCallback(async () => {
    if (shipSampleIds.length === 0) {
      toast.error("No samples selected");
      return;
    }

    const trackingNumber = shipForm.localPickup
      ? "Local Pick Up"
      : shipForm.trackingNumber.trim();

    if (!trackingNumber) {
      toast.error("Add a tracking number or mark as Local Pick Up");
      return;
    }

    setActiveBulkAction("ship");
    try {
      const result = await bulkUpdateMutation({
        sampleRequestIds: shipSampleIds as any[],
        status: "shipped",
        trackingNumber,
        carrierId: shipForm.carrierId ? (shipForm.carrierId as any) : undefined,
      });
      const updated = result?.data?.updated ?? shipSampleIds.length;
      toast.success(`Marked shipped ${updated} sample${updated === 1 ? "" : "s"}`);
      shipClearSelectionRef.current?.();
      setShipOpen(false);
      setShipSampleIds([]);
      setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to mark selected samples as shipped",
      );
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, shipForm, shipSampleIds]);

  const openRejectDialog = React.useCallback((selectedRows: any[], clearSelection: () => void) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error("No samples selected");
      return;
    }
    rejectClearSelectionRef.current = clearSelection;
    setRejectSampleIds(ids as string[]);
    setRejectReason("");
    setRejectOpen(true);
  }, [getSelectedSampleIds]);

  const handleRejectSelected = React.useCallback(async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please provide a rejection reason");
      return;
    }
    if (rejectSampleIds.length === 0) {
      toast.error("No samples selected");
      return;
    }

    setActiveBulkAction("reject");
    try {
      const result = await bulkUpdateMutation({
        sampleRequestIds: rejectSampleIds as any[],
        status: "rejected",
        rejectionReason: reason,
      });
      const updated = result?.data?.updated ?? rejectSampleIds.length;
      toast.success(`Rejected ${updated} sample${updated === 1 ? "" : "s"}`);

      rejectClearSelectionRef.current?.();
      setRejectOpen(false);
      setRejectReason("");
      setRejectSampleIds([]);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to reject selected samples",
      );
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, rejectReason, rejectSampleIds]);

  // --- Per-row handlers ---

  const handleRowApprove = React.useCallback((row: any) => {
    setApproveSampleId(row._id ?? row.id);
    setApproveNotes("");
    setApproveOpen(true);
  }, []);

  const handleConfirmApprove = React.useCallback(async () => {
    if (!approveSampleId) return;
    setIsApproving(true);
    try {
      await approveMutation({
        sampleRequestId: approveSampleId as any,
        notes: approveNotes.trim() || undefined,
      });
      toast.success("Sample request approved");
      setApproveOpen(false);
      setApproveSampleId(null);
      setApproveNotes("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve sample");
    } finally {
      setIsApproving(false);
    }
  }, [approveMutation, approveSampleId, approveNotes]);

  const handleRowFulfill = React.useCallback((row: any) => {
    const tracking = String(row.trackingNumber ?? "");
    const localPickup = isLocalPickupTracking(tracking);
    setFulfillSampleId(row._id ?? row.id);
    setFulfillForm({
      trackingNumber: localPickup ? "" : tracking,
      carrierId: String(row.carrierId ?? ""),
      notes: "",
      localPickup,
    });
    setFulfillOpen(true);
  }, []);

  const handleConfirmFulfill = React.useCallback(async () => {
    if (!fulfillSampleId) return;

    const trackingNumber = fulfillForm.localPickup
      ? "Local Pick Up"
      : fulfillForm.trackingNumber.trim();

    if (!trackingNumber) {
      toast.error("Add a tracking number or mark as Local Pick Up");
      return;
    }

    setIsFulfilling(true);
    try {
      await fulfillMutation({
        requestId: fulfillSampleId as any,
        trackingNumber,
        carrierId: fulfillForm.carrierId ? (fulfillForm.carrierId as any) : undefined,
        notes: fulfillForm.notes.trim() || undefined,
      });
      toast.success("Sample marked as shipped");
      setFulfillOpen(false);
      setFulfillSampleId(null);
      setFulfillForm({ trackingNumber: "", carrierId: "", notes: "", localPickup: false });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to fulfill sample");
    } finally {
      setIsFulfilling(false);
    }
  }, [fulfillMutation, fulfillSampleId, fulfillForm]);

  const handleRowReject = React.useCallback((row: any) => {
    setSingleRejectId(row._id ?? row.id);
    setSingleRejectReason("");
    setSingleRejectOpen(true);
  }, []);

  const handleConfirmSingleReject = React.useCallback(async () => {
    if (!singleRejectId) return;
    const reason = singleRejectReason.trim();
    if (!reason) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setIsRejecting(true);
    try {
      await rejectMutation({
        sampleRequestId: singleRejectId as any,
        rejectionReason: reason,
      });
      toast.success("Sample request rejected");
      setSingleRejectOpen(false);
      setSingleRejectId(null);
      setSingleRejectReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject sample");
    } finally {
      setIsRejecting(false);
    }
  }, [rejectMutation, singleRejectId, singleRejectReason]);

  const handleTrackingDelivered = React.useCallback(async (row: any) => {
    const sampleRequestId = row._id ?? row.id;
    if (!sampleRequestId || normalizeStatus(row.status) === "delivered") return;

    try {
      await updateSampleMutation({
        sampleRequestId: sampleRequestId as any,
        status: "delivered",
      });
      toast.success("Sample marked delivered from tracking");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to mark sample delivered");
    }
  }, [updateSampleMutation]);


  const columns = useColumns({
    onApprove: handleRowApprove,
    onFulfill: handleRowFulfill,
    onReject: handleRowReject,
    onTrackingDelivered: handleTrackingDelivered,
    canManageSamples,
  });
  const initialColumnFilters = React.useMemo<ColumnFiltersState>(() => {
    if (!customerIdFilter) return [];
    return [{ id: "customer", value: customerIdFilter }];
  }, [customerIdFilter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Samples</h1>
          <p className="text-muted-foreground mt-1">
            Manage sample requests from request to delivery
            {allSamples ? <span className="ml-1 font-medium text-foreground">({allSamples.length} total)</span> : null}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          {canManageSamples ? (
            <Button onClick={() => setCreateOpen(true)}>Create Sample</Button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sample Requests</CardTitle>
          <CardDescription>Filter by status to manage the sample pipeline.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
            <TabsList>
              {STATUS_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {allSamples === undefined || (isPending && filteredSamples.length === 0) ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={10} withPagination />
          ) : (
          <InternalEntityDataTable
            key={customerIdFilter || "all-samples"}
            data={filteredSamples.map((s: any) => ({ ...s, id: s._id || s.id }))}
            columns={columns}
            initialSorting={[{ id: "createdAt", desc: true }]}
            initialColumnFilters={initialColumnFilters}
            expandedRow={{
              render: ({ row }) => <SampleItemsExpanded row={row} />,
              getCanExpand: (row) => (row.items?.length ?? 0) > 0,
            }}
            actionBar={canManageSamples
              ? ({ selectedRows, clearSelection }) => (
                  <DataTableFloatingBar
                    selectedCount={selectedRows.length}
                    onClear={clearSelection}
                  >
                    {(() => {
                      const statuses = Array.from(
                        new Set(selectedRows.map((row) => normalizeStatus(row.status))),
                      );
                      const canApprove = statuses.length > 0
                        && statuses.every((status) => REQUEST_STATUSES.has(status));
                      const canReject = canApprove;
                      const canUndoApprove = statuses.length > 0
                        && statuses.every((status) => APPROVED_STATUSES.has(status));
                      const canMarkShipped = canUndoApprove;
                      const hasAnyAction = canApprove || canReject || canUndoApprove || canMarkShipped;

                      return (
                        <>
                          {canApprove ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={activeBulkAction !== null}
                              onClick={() =>
                                void handleBulkStatusChange(
                                  selectedRows,
                                  "approved",
                                  "approve",
                                  "Approved",
                                  clearSelection,
                                )
                              }
                            >
                              {activeBulkAction === "approve" ? "Approving..." : "Approve"}
                            </Button>
                          ) : null}
                          {canUndoApprove ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={activeBulkAction !== null}
                              onClick={() =>
                                void handleBulkStatusChange(
                                  selectedRows,
                                  "pending",
                                  "undoApprove",
                                  "Moved to Pending",
                                  clearSelection,
                                )
                              }
                            >
                              {activeBulkAction === "undoApprove" ? "Updating..." : "Undo Approve"}
                            </Button>
                          ) : null}
                          {canMarkShipped ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={activeBulkAction !== null}
                              onClick={() => openShipDialog(selectedRows, clearSelection)}
                            >
                              {activeBulkAction === "ship" ? "Updating..." : "Mark Shipped"}
                            </Button>
                          ) : null}
                          {canReject ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={activeBulkAction !== null}
                              onClick={() => openRejectDialog(selectedRows, clearSelection)}
                            >
                              Reject
                            </Button>
                          ) : null}
                          {!hasAnyAction ? (
                            <span className="px-2 py-1 text-xs text-muted-foreground">
                              No shared action for mixed statuses
                            </span>
                          ) : null}
                        </>
                      );
                    })()}
                  </DataTableFloatingBar>
                )
              : undefined}
          />
          )}
        </CardContent>
      </Card>

      {/* Bulk Reject Dialog */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(open) => {
          setRejectOpen(open);
          if (!open) {
            setRejectReason("");
            setRejectSampleIds([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reject Selected Samples</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting {rejectSampleIds.length} sample{rejectSampleIds.length === 1 ? "" : "s"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Add a rejection reason..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectOpen(false);
                setRejectReason("");
                setRejectSampleIds([]);
              }}
              disabled={activeBulkAction === "reject"}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRejectSelected()}
              disabled={activeBulkAction === "reject" || !rejectReason.trim()}
            >
              {activeBulkAction === "reject" ? "Rejecting..." : "Reject Samples"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Ship Dialog */}
      <Dialog
        open={shipOpen}
        onOpenChange={(open) => {
          setShipOpen(open);
          if (!open) {
            setShipSampleIds([]);
            setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mark Selected Samples As Shipped</DialogTitle>
            <DialogDescription>
              Add shipping details for {shipSampleIds.length} sample{shipSampleIds.length === 1 ? "" : "s"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="ship-carrier">Carrier (optional)</Label>
              <Select
                value={shipForm.carrierId || "none"}
                onValueChange={(value) =>
                  setShipForm((prev) => ({
                    ...prev,
                    carrierId: value === "none" ? "" : value,
                  }))
                }
              >
                <SelectTrigger id="ship-carrier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Carrier</SelectItem>
                  {carriers?.map((carrier: any) => (
                    <SelectItem key={carrier._id} value={carrier._id}>
                      {carrier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ship-tracking">Tracking Number</Label>
              <Input
                id="ship-tracking"
                value={shipForm.trackingNumber}
                onChange={(e) =>
                  setShipForm((prev) => ({ ...prev, trackingNumber: e.target.value }))
                }
                placeholder="Enter tracking number..."
                disabled={shipForm.localPickup}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="ship-local-pickup"
                checked={shipForm.localPickup}
                onCheckedChange={(checked) => {
                  const isChecked = Boolean(checked);
                  setShipForm((prev) => ({
                    ...prev,
                    localPickup: isChecked,
                    trackingNumber: isChecked ? "" : prev.trackingNumber,
                  }));
                }}
              />
              <Label htmlFor="ship-local-pickup" className="font-normal">
                Local Pick Up (No Tracking Required)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShipOpen(false);
                setShipSampleIds([]);
                setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
              }}
              disabled={activeBulkAction === "ship"}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleShipSelected()}
              disabled={
                activeBulkAction === "ship"
                || (!shipForm.localPickup && !shipForm.trackingNumber.trim())
              }
            >
              {activeBulkAction === "ship" ? "Saving..." : "Mark Shipped"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-row Approve Dialog */}
      <Dialog open={approveOpen} onOpenChange={(open) => { setApproveOpen(open); if (!open) { setApproveSampleId(null); setApproveNotes(""); } }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Approve Sample Request</DialogTitle>
            <DialogDescription>Optionally add notes before approving.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="approve-notes">Notes (optional)</Label>
            <Textarea
              id="approve-notes"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Any notes for the team..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={isApproving}>Cancel</Button>
            <Button onClick={() => void handleConfirmApprove()} disabled={isApproving}>
              {isApproving ? "Approving..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-row Fulfill Dialog */}
      <Dialog open={fulfillOpen} onOpenChange={(open) => { setFulfillOpen(open); if (!open) { setFulfillSampleId(null); setFulfillForm({ trackingNumber: "", carrierId: "", notes: "", localPickup: false }); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Fulfill Sample Request</DialogTitle>
            <DialogDescription>Add shipping details to mark this request as shipped.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="fulfill-carrier">Carrier (optional)</Label>
              <Select
                value={fulfillForm.carrierId || "none"}
                onValueChange={(value) =>
                  setFulfillForm((prev) => ({ ...prev, carrierId: value === "none" ? "" : value }))
                }
              >
                <SelectTrigger id="fulfill-carrier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Carrier</SelectItem>
                  {carriers?.map((carrier: any) => (
                    <SelectItem key={carrier._id} value={carrier._id}>{carrier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fulfill-tracking">Tracking Number</Label>
              <Input
                id="fulfill-tracking"
                value={fulfillForm.trackingNumber}
                onChange={(e) => setFulfillForm((prev) => ({ ...prev, trackingNumber: e.target.value }))}
                placeholder="Enter tracking number..."
                disabled={fulfillForm.localPickup}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="fulfill-local-pickup"
                checked={fulfillForm.localPickup}
                onCheckedChange={(checked) => {
                  const isChecked = Boolean(checked);
                  setFulfillForm((prev) => ({
                    ...prev,
                    localPickup: isChecked,
                    trackingNumber: isChecked ? "" : prev.trackingNumber,
                  }));
                }}
              />
              <Label htmlFor="fulfill-local-pickup" className="font-normal">Local Pick Up</Label>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fulfill-notes">Notes (optional)</Label>
              <Textarea
                id="fulfill-notes"
                value={fulfillForm.notes}
                onChange={(e) => setFulfillForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Any shipping notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFulfillOpen(false)} disabled={isFulfilling}>Cancel</Button>
            <Button
              onClick={() => void handleConfirmFulfill()}
              disabled={isFulfilling || (!fulfillForm.localPickup && !fulfillForm.trackingNumber.trim())}
            >
              {isFulfilling ? "Saving..." : "Mark Shipped"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-row Reject Dialog */}
      <Dialog open={singleRejectOpen} onOpenChange={(open) => { setSingleRejectOpen(open); if (!open) { setSingleRejectId(null); setSingleRejectReason(""); } }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Reject Sample Request</DialogTitle>
            <DialogDescription>Provide a reason for rejecting this request.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="single-reject-reason">Reason</Label>
            <Textarea
              id="single-reject-reason"
              value={singleRejectReason}
              onChange={(e) => setSingleRejectReason(e.target.value)}
              placeholder="Add a rejection reason..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSingleRejectOpen(false)} disabled={isRejecting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmSingleReject()}
              disabled={isRejecting || !singleRejectReason.trim()}
            >
              {isRejecting ? "Rejecting..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Sample Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Sample Request</DialogTitle>
            <DialogDescription>Request a new sample for a customer</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Customer</Label>
              <ComboboxSelect
                options={customerOptions}
                value={form.userId}
                onValueChange={(value) => setForm((p) => ({ ...p, userId: value }))}
                placeholder="Search and select a customer..."
                searchPlaceholder="Search by company, name, or email..."
                emptyText="No customers found."
                loading={users === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>Product</Label>
              <ComboboxSelect
                options={productComboboxOptions}
                value={form.productId}
                onValueChange={(value) => setForm((p) => ({ ...p, productId: value }))}
                placeholder="Search and select a product..."
                searchPlaceholder="Search by product, origin, or producer..."
                emptyText="No products found."
                loading={isLoadingProducts}
              />
            </div>
            <div className="grid gap-2">
              <Label>Quantity (lbs)</Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.quantity}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    quantity: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)),
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Any special instructions..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleCreate()} disabled={isCreating}>{isCreating ? "Creating..." : "Create Sample"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
